import React from "react";
import { DataGrid, GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";

import chipsColumn from "components/tables/columns/ChipsColumn";
import distributionBarColumn from "components/tables/columns/DistributionBarColumn";
import numberColumn from "components/tables/columns/NumberColumn";
import percentColumn from "components/tables/columns/PercentColumn";
import textColumn from "components/tables/columns/TextColumn";
import sentimentBarColumn from "components/tables/columns/SentimentBarColumn";
import WidgetAlert, { WidgetErrorType } from "components/widgetParts/WidgetAlert";
import WidgetLayout from "components/widgetParts/WidgetLayout";
import WidgetTitle from "components/widgetParts/WidgetTitle";
import { defaultSettings } from "./NarrativeTableSettings";
import { Query } from "system/query/Query";
import { DashboardQueryDrilldownDialog } from "components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog";
import withQueryWidget, { WithQueryWidgetProps } from "components/widgetParts/queryWidgetComposition/withQueryWidget";
import { getSingleOriginalRequestByName, getSingleWidgetResponseByName } from "system/widget/Widget";
import StripedDataGrid from "components/uiElements/dataGrid/StripedDataGrid";

interface Narrative {
  label: string;
  count: number;
  distribution: number;
  est_total_count: number;
  positive_count: number;
  negative_count: number;
  est_total_positive_count: number;
  est_total_negative_count: number;
  positivity: number;
  sentimental: number;
  key_topics: Array<string>;
  query: Query;
}

function NarrativeTableWidget(props: WithQueryWidgetProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'narrative-table');
  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'narrative-table');
  const responseBody = apiResponseInfo?.responseBody || {};

  const data: Array<Narrative> = responseBody?.clusters ?? [];
  const fieldLabels: Record<string, string> = responseBody?.field_labels ?? [];

  const [selectedRow, setSelectedRow] = React.useState<Narrative | null>(null);
  const handleRowClick = (row: Narrative) => {
    setSelectedRow(row);
  };

  const useEstTotalCount = data?.some((narrative) => narrative.count !== narrative.est_total_count);

  const columns: GridColDef[] = [
    { field: "label", ...textColumn({ headerLabels: fieldLabels }), width: 250 },
    !useEstTotalCount ? { field: "count", ...numberColumn({ headerLabels: fieldLabels }) } : { field: "est_total_count", ...numberColumn({ headerLabels: fieldLabels }) },
    { field: "distribution", ...distributionBarColumn({ headerLabels: fieldLabels }) },
    { field: "positivity", ...sentimentBarColumn({ headerLabels: fieldLabels }) },
    { field: "positive_count", ...numberColumn({ headerLabels: fieldLabels }) },
    { field: "negative_count", ...numberColumn({ headerLabels: fieldLabels }) },
    { field: "est_total_positive_count", ...numberColumn({ headerLabels: fieldLabels }) },
    { field: "est_total_negative_count", ...numberColumn({ headerLabels: fieldLabels }) },
    { field: "sentimental", ...percentColumn({ headerLabels: fieldLabels }) },
    { field: "key_topics", ...chipsColumn({ headerLabels: fieldLabels }) }
  ];

  const columnVisibilityModel: GridColumnVisibilityModel = {
    positive_count: false,
    negative_count: false,
    est_total_positive_count: false,
    est_total_negative_count: false,
    sentimental: false,
    key_topics: false
  };

  const currentState = {
    ...defaultSettings,
    ...props.widget?.data?.settings
  };

  return (
    <>
      {data.length !== 0 &&
        <WidgetLayout
          header={
            <WidgetTitle
              alignment={currentState.widgetTitleAlignment}
              customTitle={currentState.customWidgetTitle}
              // defaultTitle="Narratives Table"
              defaultTitle={i18n.t("widget-types.narrative-table.label")}
              settingsTitle={currentState.widgetTitle}
              style={{ pb: 2 }}
              widgetQueryTitle={apiResponseInfo?.queryDisplayTitle} />
          }
        >
          <StripedDataGrid
            showRowStripes={currentState.rowStripes}
            columns={columns}
            density={currentState?.density ?? "standard"}
            getRowId={(row: Narrative) => row.label}
            initialState={{
              columns: {
                columnVisibilityModel
              },
              sorting: {
                sortModel: [{field: "count", sort: "desc"}]
              }
            }}
            rows={data}
            onRowClick={(event) => {
              handleRowClick(event.row);
            }}
          />
        </WidgetLayout>
      }
      {data.length === 0 &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
      {selectedRow &&
        <DashboardQueryDrilldownDialog
          title={selectedRow.label ?? ""}
          queryInfo={props.queryInfo}
          onClose={() => setSelectedRow(null)}
          activeQueryIds={apiResponseInfo?.originatingRequest?.activeQueryIds}
          drilldownQuery={new Query({ query: selectedRow?.query })}
          widgetRequestInfo={originalRequest}
        />
      }
    </>
  );
}

export default withQueryWidget(NarrativeTableWidget, {
    hasSettingsDialog: true,
    hasQueryDialog: true,
});
